import * as React from 'react';
import { ScrollArrowButton } from './components/scroll-arrow-button';
import { useHorizontalScroll } from './hooks/use-horizontal-scroll';
import type { HorizontalScrollListProps } from './horizontal-scroll-list.types';
import { cn } from '@utils';

/** Tailwind gap multiplier: 1 unit = 0.25rem */
const GAP_MULTIPLIER = 0.25;

/**
 * HorizontalScrollList - A container that lays children out in a single horizontal
 * row. Caps its width at the parent's width and scrolls horizontally when the
 * combined width of the children exceeds the available space. The native scrollbar
 * is hidden across browsers; the row remains scrollable via wheel, touch, and
 * keyboard. When `showNavigationArrows` is enabled (default) circular arrow buttons
 * overlay the edges and are disabled when the row cannot scroll further in that
 * direction.
 *
 * `min-w-0` ensures the container can shrink below its intrinsic content size when
 * placed inside a flex parent, so the overflow can scroll instead of pushing the
 * parent wider.
 */
const HorizontalScrollList = <T,>({
	children,
	gap = 4,
	className,
	style,
	items,
	ItemComponent,
	index,
	onIntersect,
	showNavigationArrows = true,
	...props
}: HorizontalScrollListProps<T>) => {
	const observerRef = React.useRef<IntersectionObserver | null>(null);
	const { containerRef, canScrollLeft, canScrollRight, verticalOffset, scrollTo } = useHorizontalScroll(
		items?.length ?? 0
	);

	const handleScrollLeft = React.useCallback(() => scrollTo('left'), [scrollTo]);
	const handleScrollRight = React.useCallback(() => scrollTo('right'), [scrollTo]);

	const containerStyle: React.CSSProperties = {
		gap: `${gap * GAP_MULTIPLIER}rem`,
		...style,
	};

	const handleIntersect = React.useCallback(
		(node: HTMLElement) => {
			observerRef.current = new IntersectionObserver(([entry]) => {
				if (!entry.isIntersecting) {
					return;
				}

				onIntersect?.();
				observerRef.current!.unobserve(node);
			});

			if (node) {
				observerRef.current.observe(node);
			}
		},
		[onIntersect]
	);

	React.useEffect(() => {
		return () => {
			observerRef.current?.disconnect();
		};
	}, []);

	const isScrollable = canScrollLeft || canScrollRight;
	const showNavigationButtons: boolean = showNavigationArrows && Boolean(items?.length) && isScrollable;

	return (
		<div
			className={cn('relative flex w-full min-w-0 items-center justify-center', showNavigationButtons && 'gap-2')}
			data-testid="horizontal-scroll-list-wrapper"
		>
			{showNavigationButtons && (
				<ScrollArrowButton direction="left" onClick={handleScrollLeft} top={verticalOffset} disabled={!canScrollLeft} />
			)}
			<div
				ref={containerRef}
				className={cn(
					'flex w-full max-w-full min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>*]:shrink-0',
					className
				)}
				style={containerStyle}
				data-aid="horizontal-scroll-list"
				{...props}
			>
				{items?.map((item, i) => <ItemComponent key={i} item={item} ref={i === index ? handleIntersect : undefined} />)}
			</div>
			{showNavigationButtons && (
				<ScrollArrowButton
					direction="right"
					onClick={handleScrollRight}
					top={verticalOffset}
					disabled={!canScrollRight}
				/>
			)}
		</div>
	);
};

HorizontalScrollList.displayName = 'HorizontalScrollList';

export { HorizontalScrollList };
